import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

const CompanyLocations = () => {
  const [activeTab, setActiveTab] = useState("main");
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [hoveredButton, setHoveredButton] = useState(null);
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Reset map loaded state when tab changes
  useEffect(() => {
    setIsMapLoaded(false);
  }, [activeTab]);

  // Check if we're on mobile
  const isMobile = windowWidth <= 768;
  
  const locations = {
  main: {
    name: "Headquarters",
    address: "342, Vardhman Nagar, Badarwas, Ajmer Road",
    city: "Jaipur- 302019",
    phone: "+91 9887-772727",
    email: "info@deeceeelevators.com",
    hours: "Monday - Saturday: 9:00 AM - 6:00 PM",
    mapUrl:
      "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1382.9212127191008!2d75.73668039248157!3d26.880882121144943!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x396db53d78880f37%3A0x68323c1e6dcd58bf!2sDee%20Cee%20Elevator%20Private%20Limited!5e0!3m2!1sen!2sin!4v1747754445853!5m2!1sen!2sin",
    features: [
      "Manufacturing Facility",
      "Design Studio",
      "Customer Experience Center",
      "Test Tower",
    ],
    image: "/images/about/headquarters.jpg",
  },
  branch: {
    name: "Agra Office",
    address: "2/49, Ramnagar Colony, Civil Lines",
    city: "Agra, Uttar Pradesh 282002",
    phone: "+91 9837-221980",
    email: "info@deeceeelevators.in",
    hours: "Monday - Friday: 10:00 AM - 5:00 PM",
    mapUrl:
      "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3549.865675188357!2d77.99832!3d27.189671!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x397479fd4246ace3%3A0xbe43ef43a7f1b0f2!2sRamnagar%20Colony%2C%20Civil%20Lines%2C%20Agra%2C%20Uttar%20Pradesh%20282002!5e0!3m2!1sen!2sin!4v1650134560184!5m2!1sen!2sin",
    features: ["Sales Office", "Consultation Center", "Project Gallery"],
    image: "/images/about/agra-branch.jpg",
  },
};

  const activeLocation = locations[activeTab];

  // Dynamic styles for responsive design
  const responsiveStyles = {
    section: {
      padding: isMobile ? "60px 0" : "120px 0",
    },
    title: {
      fontSize: isMobile ? "2.2rem" : "3rem",
    },
    subtitle: {
      fontSize: isMobile ? "1rem" : "1.1rem",
      padding: isMobile ? "0 15px" : "0",
    },
    content: {
      flexDirection: isMobile ? "column" : "row",
      gap: isMobile ? "40px" : "30px",
    },
    mapContainer: {
      height: isMobile ? "380px" : "520px",
    },
    locationName: {
      fontSize: isMobile ? "1.4rem" : "1.6rem",
    },
    addressCard: {
      padding: isMobile ? "20px" : "30px",
    },
  };

  return (
    <section style={{...styles.section, ...responsiveStyles.section}}>
      {/* Decorative background elements */}
      <div style={styles.decorativeBg1}></div>
      <div style={styles.decorativeBg2}></div>
      <div style={styles.decorativeLine}></div>

      <div style={styles.container}>
        <div style={styles.header}>
          <div style={styles.titleWrapper}>
            <span style={styles.titleAccent}></span>
            <h2 style={{...styles.title, ...responsiveStyles.title}}>Our Locations</h2>
          </div>
          <p style={{...styles.subtitle, ...responsiveStyles.subtitle}}>
            Visit us to experience the future of vertical transportation
          </p>
        </div>

        <div style={styles.tabContainer}>
          {Object.keys(locations).map((loc) => (
            <motion.div
              key={loc}
              style={{
                ...styles.tab,
                ...(activeTab === loc ? styles.activeTab : {}),
                ...(isMobile ? { padding: "12px 20px", minWidth: "140px" } : {}),
              }}
              onClick={() => setActiveTab(loc)}
              whileHover={{ y: -2, boxShadow: "0 8px 20px rgba(0, 0, 0, 0.1)" }}
              whileTap={{ scale: 0.98 }}
            >
              <span style={styles.tabIcon}>
                <i
                  className={
                    loc === "main" ? "fas fa-building" : "fas fa-store"
                  }
                ></i>
              </span>
              <span style={styles.tabName}>{locations[loc].name}</span>
              {activeTab === loc && (
                <motion.div
                  layoutId="tabIndicator"
                  style={styles.tabIndicator}
                />
              )}
            </motion.div>
          ))}
        </div>

        <div style={styles.contentContainer}>
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
              style={{...styles.content, ...responsiveStyles.content}}
            >
              <div style={styles.infoSection}>
                <motion.div
                  style={{...styles.addressCard, ...responsiveStyles.addressCard}}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3, duration: 0.5 }}
                >
                  <div style={styles.cardHeader}>
                    <div style={styles.locationIcon}>
                      <i className="fas fa-map-marker-alt"></i>
                    </div>
                    <div style={styles.headerContent}>
                      <h3 style={{...styles.locationName, ...responsiveStyles.locationName}}>{activeLocation.name}</h3>
                      <div style={styles.locationStatus}>
                        <span style={styles.statusIndicator}></span>
                        Open Now
                      </div>
                    </div>
                  </div>

                  <div style={styles.addressDetails}>
                    <p style={styles.address}>
                      {activeLocation.address}
                      <br />
                      {activeLocation.city}
                    </p>

                    <div style={styles.contactInfo}>
                      {[
                        {
                          icon: "fas fa-phone-alt",
                          content: activeLocation.phone,
                        },
                        {
                          icon: "fas fa-envelope",
                          content: activeLocation.email,
                        },
                        { icon: "fas fa-clock", content: activeLocation.hours },
                      ].map((item, index) => (
                        <motion.div
                          key={index}
                          style={styles.contactItem}
                          whileHover={{ x: 3 }}
                        >
                          <span style={styles.contactIcon}>
                            <i className={item.icon}></i>
                          </span>
                          <span style={{ fontSize: isMobile ? "0.95rem" : "1rem" }}>{item.content}</span>
                        </motion.div>
                      ))}
                    </div>
                  </div>

                  <div style={styles.features}>
                    <h4 style={styles.featuresTitle}>
                      <span style={styles.featuresTitleIcon}>
                        <i className="fas fa-star"></i>
                      </span>
                      Location Features
                    </h4>
                    <div style={styles.featuresList}>
                      {activeLocation.features.map((feature, index) => (
                        <motion.div
                          key={index}
                          style={styles.featureItem}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.4 + index * 0.1 }}
                          whileHover={{ x: 5 }}
                        >
                          <span style={styles.featureIcon}>
                            <i className="fas fa-check-circle"></i>
                          </span>
                          <span style={styles.featureText}>{feature}</span>
                        </motion.div>
                      ))}
                    </div>
                  </div>

                  <div style={styles.ctaSection}>
                    <motion.a
                      href={`https://maps.google.com/?q=${activeLocation.address}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        ...styles.directionButton,
                        ...(hoveredButton === "directions"
                          ? styles.directionButtonHover
                          : {}),
                        ...(isMobile ? { padding: "14px 16px" } : {}),
                      }}
                      onMouseEnter={() => setHoveredButton("directions")}
                      onMouseLeave={() => setHoveredButton(null)}
                      whileHover={{ y: -3 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <span style={styles.buttonIcon}>
                        <i className="fas fa-directions"></i>
                      </span>
                      Get Directions
                      <span style={styles.buttonHighlight}></span>
                    </motion.a>
                    <motion.a
                      href={`tel:${activeLocation.phone.replace(/\D/g, "")}`}
                      style={{
                        ...styles.callButton,
                        ...(hoveredButton === "call"
                          ? styles.callButtonHover
                          : {}),
                        ...(isMobile ? { padding: "14px 16px" } : {}),
                      }}
                      onMouseEnter={() => setHoveredButton("call")}
                      onMouseLeave={() => setHoveredButton(null)}
                      whileHover={{ y: -3 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <span style={styles.buttonIcon}>
                        <i className="fas fa-phone"></i>
                      </span>
                      Call Now
                      <span style={styles.buttonHighlight}></span>
                    </motion.a>
                  </div>
                </motion.div>
              </div>

              <div style={{
                ...styles.mapSection, 
                minWidth: isMobile ? "100%" : "450px"
              }}>
                <motion.div
                  style={styles.mapWrapper}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4, duration: 0.5 }}
                >
                  {/* Map Title Banner */}
                  <div style={styles.mapTitleBanner}>
                    <div style={styles.mapTitleIcon}>
                      <i className="fas fa-map-marked-alt"></i>
                    </div>
                    <div style={styles.mapTitleText}>
                      Find us at {activeLocation.name}
                    </div>
                  </div>

                  <div style={{...styles.mapContainer, ...responsiveStyles.mapContainer}}>
                    {/* Decorative elements */}
                    <div
                      style={{
                        ...styles.mapDecorativeCorner,
                        top: "16px",
                        left: "16px",
                        borderTopWidth: "3px",
                        borderLeftWidth: "3px",
                        borderTopLeftRadius: "8px",
                      }}
                    ></div>
                    <div
                      style={{
                        ...styles.mapDecorativeCorner,
                        top: "16px",
                        right: "16px",
                        borderTopWidth: "3px",
                        borderRightWidth: "3px",
                        borderTopRightRadius: "8px",
                      }}
                    ></div>
                    <div
                      style={{
                        ...styles.mapDecorativeCorner,
                        bottom: "16px",
                        left: "16px",
                        borderBottomWidth: "3px",
                        borderLeftWidth: "3px",
                        borderBottomLeftRadius: "8px",
                      }}
                    ></div>
                    <div
                      style={{
                        ...styles.mapDecorativeCorner,
                        bottom: "16px",
                        right: "16px",
                        borderBottomWidth: "3px",
                        borderRightWidth: "3px",
                        borderBottomRightRadius: "8px",
                      }}
                    ></div>

                    {/* Loading state */}
                    {!isMapLoaded && (
                      <div style={styles.mapLoader}>
                        <div style={{
                          ...styles.spinner,
                          width: isMobile ? "40px" : "48px",
                          height: isMobile ? "40px" : "48px",
                        }}></div>
                        <div style={styles.loaderText}>
                          Loading interactive map...
                        </div>
                      </div>
                    )}

                    {/* Actual map */}
                    <iframe
                      src={activeLocation.mapUrl}
                      width="100%"
                      height="100%"
                      style={{ border: 0, borderRadius: "8px" }}
                      allowFullScreen=""
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      onLoad={() => setIsMapLoaded(true)}
                      title={`Map of ${activeLocation.name}`}
                    ></iframe>

                    {/* Map controls - position differently on mobile */}
                    <div style={{
                      ...styles.mapControls,
                      flexDirection: isMobile ? "row" : "column",
                      top: isMobile ? "auto" : "24px",
                      bottom: isMobile ? "24px" : "auto",
                    }}>
                      <motion.a
                        href={`https://maps.google.com/?q=${activeLocation.address}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          ...styles.mapControl,
                          width: isMobile ? "45px" : "40px",
                          height: isMobile ? "45px" : "40px",
                        }}
                        whileHover={{
                          y: -2,
                          backgroundColor: "#1e4fc9",
                          color: "white",
                        }}
                        whileTap={{ scale: 0.97 }}
                      >
                        <i className="fas fa-external-link-alt"></i>
                      </motion.a>
                      <motion.button
                        style={{
                          ...styles.mapControl,
                          width: isMobile ? "45px" : "40px",
                          height: isMobile ? "45px" : "40px",
                        }}
                        whileHover={{
                          y: -2,
                          backgroundColor: "#1e4fc9",
                          color: "white",
                        }}
                        whileTap={{ scale: 0.97 }}
                        onClick={() => {
                          if (navigator.share) {
                            navigator.share({
                              title: `${activeLocation.name} - DeeCee Elevators`,
                              text: `${activeLocation.address}, ${activeLocation.city}`,
                              url: `https://maps.google.com/?q=${activeLocation.address}`,
                            });
                          }
                        }}
                      >
                        <i className="fas fa-share-alt"></i>
                      </motion.button>
                    </div>
                  </div>

                  {/* Quick Info - collapse to single line on small mobile screens */}
                  <div style={{
                    ...styles.mapQuickInfo,
                    flexDirection: windowWidth <= 480 ? "column" : "column",
                    padding: isMobile ? "12px 16px" : "16px 24px",
                  }}>
                    <div style={styles.quickInfoItem}>
                      <div style={styles.quickInfoIcon}>
                        <i className="fas fa-map-pin"></i>
                      </div>
                      <div style={{
                        ...styles.quickInfoText,
                        fontSize: isMobile ? "0.85rem" : "0.9rem"
                      }}>
                        {activeLocation.address}, {activeLocation.city}
                      </div>
                    </div>
                    <div style={styles.quickInfoItem}>
                      <div style={styles.quickInfoIcon}>
                        <i className="fas fa-clock"></i>
                      </div>
                      <div style={{
                        ...styles.quickInfoText,
                        fontSize: isMobile ? "0.85rem" : "0.9rem"
                      }}>
                        {activeLocation.hours}
                      </div>
                    </div>
                  </div>
                </motion.div>

                <div style={styles.mapShadow}></div>

                <div style={{
                  ...styles.mapCredits,
                  bottom: isMobile ? "-30px" : "-35px",
                }}>
                  <i
                    className="fas fa-map-marked-alt"
                    style={styles.mapIcon}
                  ></i>
                  <span style={styles.mapText}>Powered by Google Maps</span>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Add the animation keyframes */}
      <style jsx global>{`
        @keyframes shimmer {
          0% { left: -100%; }
          100% { left: 100%; }
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @keyframes pulse {
          0% { opacity: 0.5; }
          50% { opacity: 1; }
          100% { opacity: 0.5; }
        }
      `}</style>
    </section>
  );
};

const styles = {
  /* Keeping all your existing styles */
  section: {
    background: "linear-gradient(to bottom, #f8fafc, #f1f5f9)",
    position: "relative",
    overflow: "hidden",
  },
  decorativeBg1: {
    position: "absolute",
    top: "5%",
    right: "5%",
    width: "300px",
    height: "300px",
    borderRadius: "50%",
    background:
      "radial-gradient(circle, rgba(30, 79, 201, 0.05) 0%, rgba(30, 79, 201, 0.02) 70%)",
    filter: "blur(40px)",
    zIndex: 0,
  },
  decorativeBg2: {
    position: "absolute",
    bottom: "10%",
    left: "5%",
    width: "250px",
    height: "250px",
    borderRadius: "50%",
    background:
      "radial-gradient(circle, rgba(74, 222, 128, 0.05) 0%, rgba(74, 222, 128, 0.02) 70%)",
    filter: "blur(40px)",
    zIndex: 0,
  },
  decorativeLine: {
    position: "absolute",
    top: "0",
    left: "50%",
    width: "1px",
    height: "100px",
    background:
      "linear-gradient(to bottom, transparent, rgba(30, 79, 201, 0.2), transparent)",
    zIndex: 1,
  },
  container: {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "0 20px",
    position: "relative",
    zIndex: 1,
  },
  header: {
    textAlign: "center",
    marginBottom: "60px",
    position: "relative",
  },
  titleWrapper: {
    position: "relative",
    display: "inline-block",
    marginBottom: "10px",
  },
  titleAccent: {
    position: "absolute",
    bottom: "0",
    left: "50%",
    transform: "translateX(-50%)",
    height: "8px",
    width: "40%",
    background:
      "linear-gradient(90deg, rgba(74, 222, 128, 0.5), rgba(30, 79, 201, 0.5))",
    borderRadius: "4px",
    zIndex: -1,
    opacity: 0.5,
  },
  title: {
    fontWeight: "800",
    color: "#1e293b",
    marginBottom: "16px",
    position: "relative",
    display: "inline-block",
    textTransform: "uppercase",
    letterSpacing: "1px",
  },
  subtitle: {
    color: "#64748b",
    maxWidth: "700px",
    margin: "0 auto",
    lineHeight: "1.6",
  },
  tabContainer: {
    display: "flex",
    justifyContent: "center",
    gap: "20px",
    marginBottom: "50px",
    flexWrap: "wrap",
  },
  tab: {
    display: "flex",
    alignItems: "center",
    padding: "14px 28px",
    backgroundColor: "#ffffff",
    borderRadius: "10px",
    boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
    cursor: "pointer",
    transition: "all 0.3s ease",
    border: "1px solid #e2e8f0",
    position: "relative",
    overflow: "hidden",
    minWidth: "180px",
    justifyContent: "center",
  },
  activeTab: {
    backgroundColor: "#1e4fc9",
    color: "white",
    transform: "translateY(-4px)",
    boxShadow: "0 15px 25px rgba(30, 79, 201, 0.25)",
    border: "1px solid #1e4fc9",
  },
  tabIcon: {
    fontSize: "1.2rem",
    marginRight: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "24px",
    height: "24px",
  },
  tabName: {
    fontWeight: "600",
    fontSize: "1.05rem",
  },
  tabIndicator: {
    position: "absolute",
    bottom: "0",
    left: "0",
    width: "100%",
    height: "4px",
    backgroundColor: "#4ade80",
    borderTopLeftRadius: "2px",
    borderTopRightRadius: "2px",
  },
  contentContainer: {
    position: "relative",
    minHeight: "600px",
  },
  content: {
    display: "flex",
    position: "relative",
    width: "100%",
    alignItems: "stretch",
  },
  infoSection: {
    flex: "1",
    minWidth: "320px",
    display: "flex",
    flexDirection: "column",
    gap: "25px",
  },
  addressCard: {
    backgroundColor: "white",
    borderRadius: "16px",
    boxShadow: "0 15px 35px rgba(0, 0, 0, 0.08)",
    border: "1px solid #e2e8f0",
    flex: "1",
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    marginBottom: "28px",
  },
  locationIcon: {
    width: "56px",
    height: "56px",
    borderRadius: "12px",
    background: "linear-gradient(135deg, #1e4fc9, #3b82f6)",
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "1.6rem",
    marginRight: "18px",
    boxShadow: "0 10px 20px rgba(30, 79, 201, 0.2)",
    position: "relative",
    overflow: "hidden",
  },
  headerContent: {
    display: "flex",
    flexDirection: "column",
  },
  locationStatus: {
    display: "flex",
    alignItems: "center",
    fontSize: "0.85rem",
    color: "#10b981",
    fontWeight: "500",
    marginTop: "4px",
  },
  statusIndicator: {
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    backgroundColor: "#10b981",
    marginRight: "6px",
    animation: "pulse 2s infinite",
  },
  locationName: {
    fontWeight: "700",
    color: "#1e293b",
    margin: 0,
  },
  addressDetails: {
    marginBottom: "28px",
  },
  address: {
    fontSize: "1.1rem",
    color: "#334155",
    marginBottom: "24px",
    lineHeight: "1.6",
  },
  contactInfo: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  contactItem: {
    display: "flex",
    alignItems: "center",
    color: "#475569",
    fontSize: "1rem",
    transition: "transform 0.3s ease",
  },
  contactIcon: {
    width: "32px",
    height: "32px",
    borderRadius: "50%",
    backgroundColor: "#f1f5f9",
    color: "#1e4fc9",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.9rem",
    marginRight: "12px",
  },
  features: {
    borderTop: "1px solid #e2e8f0",
    paddingTop: "28px",
    marginBottom: "28px",
  },
  featuresTitle: {
    fontSize: "1.15rem",
    fontWeight: "700",
    color: "#1e293b",
    marginBottom: "20px",
    display: "flex",
    alignItems: "center",
  },
  featuresTitleIcon: {
    width: "28px",
    height: "28px",
    borderRadius: "8px",
    backgroundColor: "#f0fdf4",
    color: "#4ade80",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.9rem",
    marginRight: "10px",
  },
  featuresList: {
    display: "flex",
    flexDirection: "column",
    gap: "14px",
    paddingLeft: "8px",
  },
  featureItem: {
    display: "flex",
    alignItems: "center",
    transition: "transform 0.2s ease",
  },
  featureIcon: {
    color: "#4ade80",
    marginRight: "12px",
    fontSize: "1.1rem",
    display: "flex",
    alignItems: "center",
  },
  featureText: {
    color: "#475569",
    fontSize: "1rem",
    fontWeight: "500",
  },
  ctaSection: {
    display: "flex",
    gap: "16px",
    flexWrap: "wrap",
  },
  directionButton: {
    flex: "1",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "16px 24px",
    backgroundColor: "#1e4fc9",
    color: "white",
    borderRadius: "12px",
    fontWeight: "600",
    textDecoration: "none",
    transition: "all 0.3s ease",
    boxShadow: "0 10px 20px rgba(30, 79, 201, 0.15)",
    minWidth: "150px",
    position: "relative",
    overflow: "hidden",
    fontSize: "1rem",
    zIndex: 1,
  },
  directionButtonHover: {
    backgroundColor: "#1a45b4",
    boxShadow: "0 15px 25px rgba(30, 79, 201, 0.25)",
  },
  callButton: {
    flex: "1",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "16px 24px",
    backgroundColor: "white",
    color: "#1e4fc9",
    borderRadius: "12px",
    fontWeight: "600",
    textDecoration: "none",
    transition: "all 0.3s ease",
    border: "2px solid #1e4fc9",
    boxShadow: "0 8px 15px rgba(0, 0, 0, 0.05)",
    minWidth: "150px",
    position: "relative",
    overflow: "hidden",
    fontSize: "1rem",
    zIndex: 1,
  },
  callButtonHover: {
    backgroundColor: "#f8fafc",
    borderColor: "#1a45b4",
    color: "#1a45b4",
    boxShadow: "0 12px 20px rgba(0, 0, 0, 0.08)",
  },
  buttonIcon: {
    marginRight: "10px",
    fontSize: "1.05rem",
  },
  buttonHighlight: {
    position: "absolute",
    top: "0",
    left: "-100%",
    width: "100%",
    height: "100%",
    background:
      "linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)",
    transition: "all 0.6s ease",
    zIndex: -1,
    animation: "shimmer 2s infinite",
  },
  mapSection: {
    flex: "1.5", 
    position: "relative",
    display: "flex",
    flexDirection: "column",
  },
  mapWrapper: {
    width: "100%",
    borderRadius: "20px",
    overflow: "visible",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    backgroundColor: "white",
    boxShadow: "0 20px 40px rgba(0, 0, 0, 0.08)",
  },
  mapTitleBanner: {
    display: "flex",
    alignItems: "center",
    padding: "16px 24px",
    background: "linear-gradient(135deg, #1e4fc9, #3b82f6)",
    borderTopLeftRadius: "20px",
    borderTopRightRadius: "20px",
    color: "white",
  },
  mapTitleIcon: {
    width: "36px",
    height: "36px",
    borderRadius: "50%",
    backgroundColor: "rgba(255, 255, 255, 0.2)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: "14px",
    fontSize: "1.1rem",
  },
  mapTitleText: {
    fontSize: "1.2rem",
    fontWeight: "600",
    letterSpacing: "0.5px",
  },
  mapContainer: {
    width: "100%",
    position: "relative",
    padding: "16px",
    background: "linear-gradient(to bottom, #f1f5f9, #ffffff)",
    boxSizing: "border-box",
  },
  mapDecorativeCorner: {
    position: "absolute",
    width: "24px",
    height: "24px",
    borderColor: "#4ade80",
    borderStyle: "solid",
    borderWidth: "0",
    zIndex: 2,
    pointerEvents: "none",
  },
  mapLoader: {
    position: "absolute",
    top: "16px",
    left: "16px",
    right: "16px",
    bottom: "16px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#f8fafc",
    zIndex: 5,
    gap: "16px",
    borderRadius: "8px",
  },
  spinner: {
    border: "4px solid rgba(30, 79, 201, 0.1)",
    borderTop: "4px solid #1e4fc9",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  },
  loaderText: {
    color: "#64748b",
    fontSize: "1rem",
    fontWeight: "500",
  },
  mapControls: {
    position: "absolute",
    top: "24px",
    right: "24px",
    display: "flex",
    gap: "10px",
    zIndex: 3,
  },
  mapControl: {
    width: "40px",
    height: "40px",
    borderRadius: "8px",
    backgroundColor: "white",
    color: "#1e293b",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
    border: "none",
    cursor: "pointer",
    transition: "all 0.3s ease",
    fontSize: "1rem",
  },
  mapQuickInfo: {
    padding: "16px 24px",
    borderTop: "1px solid #e2e8f0",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    borderBottomLeftRadius: "20px",
    borderBottomRightRadius: "20px",
  },
  quickInfoItem: {
    display: "flex",
    alignItems: "center",
    color: "#475569",
  },
  quickInfoIcon: {
    width: "32px",
    height: "32px",
    borderRadius: "50%",
    backgroundColor: "#f1f5f9",
    color: "#1e4fc9",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: "12px",
    fontSize: "0.9rem",
  },
  quickInfoText: {
    lineHeight: 1.5,
  },
  mapShadow: {
    position: "absolute",
    bottom: "-20px",
    left: "10%",
    width: "80%",
    height: "40px",
    borderRadius: "50%",
    backgroundColor: "rgba(0, 0, 0, 0.06)",
    filter: "blur(20px)",
    zIndex: -1,
  },
  mapCredits: {
    position: "absolute",
    right: "10px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    color: "#64748b",
    fontSize: "0.8rem",
  },
  mapIcon: {
    color: "#1e4fc9",
  },
};

export default CompanyLocations;